import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { typeLabel, type Media } from "@/mocks/media";

interface HeroCarouselProps {
  items: Media[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);
  const paused = useRef(false);

  useEffect(() => {
    const tick = () => {
      if (!paused.current) setIndex((prev) => (prev + 1) % items.length);
    };
    timer.current = window.setInterval(tick, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  const current = items[index];

  if (!current) return null;

  return (
    <section
      aria-label="焦点影视推荐"
      className="relative w-full h-[440px] md:h-[520px] bg-primary-950 overflow-hidden"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <div className="absolute inset-0">
            <img
              src={item.cover}
              alt={`${item.title} 焦点推荐`}
              title={`${item.title} ${typeLabel[item.type]}`}
              className="w-full h-full object-cover object-top blur-[2px] scale-105"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/40"></div>
        </div>
      ))}

      <div className="relative z-10 w-full h-full px-4 md:px-6 xl:px-10 flex items-center">
        <div className="w-full max-w-6xl mx-auto flex items-center gap-8 md:gap-12">
          <div
            key={current.id}
            className="hidden sm:block w-[190px] md:w-[240px] shrink-0 rounded-lg overflow-hidden border border-background-50/15 animate-fade-up"
          >
            <div className="w-full aspect-[6/8.4] bg-background-800">
              <img
                src={current.cover}
                alt={`${current.title} 海报`}
                title={`${current.title} ${typeLabel[current.type]}`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div key={`info-${current.id}`} className="flex-1 min-w-0 animate-fade-up">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2.5 h-6 inline-flex items-center rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                {typeLabel[current.type]}
              </span>
              <span className="px-2.5 h-6 inline-flex items-center rounded-md bg-secondary-500 text-foreground-950 text-xs font-bold">
                <i className="ri-star-fill mr-1 text-[10px]"></i>
                {current.rating.toFixed(1)}
              </span>
              <span className="text-xs text-background-200">
                {current.year} · {current.region} · {current.genres.join(" / ")}
              </span>
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
              {current.title}
            </h2>
            <p className="mt-3 text-sm md:text-[15px] leading-7 text-background-200 max-w-xl line-clamp-3">
              {current.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="h-10 px-5 inline-flex items-center rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-circle-line mr-1.5 text-base"></i>
                查看详情
              </Link>
              <Link
                to={`/category/${current.type}`}
                className="h-10 px-5 inline-flex items-center rounded-md border border-background-50/30 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                <i className="ri-film-line mr-1.5"></i>
                更多{typeLabel[current.type]}
              </Link>
              <span className="text-xs text-background-300 hidden md:inline">{current.status}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-7 bg-secondary-400" : "w-3 bg-background-50/40 hover:bg-background-50/70"
            }`}
          />
        ))}
      </div>

      <button
        type="button"
        aria-label="上一个"
        onClick={() => setIndex((prev) => (prev - 1 + items.length) % items.length)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full items-center justify-center bg-primary-950/40 text-background-50 hover:bg-primary-950/70 transition-colors"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>
      <button
        type="button"
        aria-label="下一个"
        onClick={() => setIndex((prev) => (prev + 1) % items.length)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full items-center justify-center bg-primary-950/40 text-background-50 hover:bg-primary-950/70 transition-colors"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>
    </section>
  );
}